import { Button } from '@synoptic/ui-kit/button/button.js';
import {
  SelectContent,
  SelectTrigger,
} from '@synoptic/ui-kit/select/select.js';
import { Select } from '@synoptic/ui-kit/select/select.js';
import { PropsWithChildren } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AiFilterSuggestion } from '@/__generated__/schema-types';
import { ConfirmModal } from '@/components/confirm-modal/confirm-modal';
import { AiFilterFormValues } from '../types';
import { useAiFilterEvaluation } from '../use-ai-filter-evaluation';
import { CreateWithAIAssistantModal } from './create-with-ai-assistant';
import { FilterQualityAlert } from './filter-quality-alert';
import { ManualFilterFormFields } from './manual-filter-form-fields';
import { TestAIFilterModal } from './test-ai-filter';
import { aiFilterSelectItem } from '../ai-filters.css';
import { aiFilterSelectTrigger } from '../ai-filters.css';
import {
  footerButtonsContainer,
  footerContainer,
  formContainer,
  formContent,
  formFieldsContainer,
} from './manage-filter.css';
interface AIFilterFormProps {
  onSubmit: (form: AiFilterFormValues) => void;
  onCancel: () => void;
  hasAcceptedPolicy: boolean;
  isEdit: boolean;
  modelSelectOptions: { content: string; value: string }[];
  formData: UseFormReturn<AiFilterFormValues>;
  onConfirmLeave: () => void;
  isConfirmLeaveModalOpen: boolean;
  setIsConfirmLeaveModalOpen: (isOpen: boolean) => void;
}

export const AIFilterForm = ({
  formData,
  onSubmit,
  onCancel,
  hasAcceptedPolicy,
  children,
  isEdit,
  modelSelectOptions,
  onConfirmLeave,
  isConfirmLeaveModalOpen,
  setIsConfirmLeaveModalOpen,
}: PropsWithChildren<AIFilterFormProps>) => {
  const { handleSubmit, formState, control, getValues } = formData;

  const isFormInvalid = !formState.isValid || !hasAcceptedPolicy;

  const {
    aiFilterEvaluation,
    evaluateFilter,
    isLoading: isLoadingAiFilterEvaluation,
    error: aiFilterEvaluationError,
  } = useAiFilterEvaluation({
    prompt: getValues('prompt'),
    passValue: getValues('passValue')!,
    model: getValues('model'),
  });

  const evaluateFilterOnValidForm = (newValue: Partial<AiFilterFormValues>) => {
    const filterData = {
      prompt: newValue.prompt || getValues('prompt'),
      passValue: newValue.passValue! || getValues('passValue')!,
      model: newValue.model || getValues('model'),
    };
    if (filterData.prompt && filterData.passValue && filterData.model) {
      evaluateFilter(filterData);
    }
  };

  return (
    <form
      className={formContainer}
      onSubmit={(e) => {
        e.stopPropagation();
        handleSubmit(onSubmit)(e);
      }}
    >
      <div className={formContent}>
        {!isEdit && (
          <CreateWithAIAssistantModal
            onSelectFilterSuggestion={(
              filterSuggestion: AiFilterSuggestion,
            ) => {
              formData.setValue('prompt', filterSuggestion.prompt, {
                shouldValidate: true,
              });
              formData.setValue('passValue', filterSuggestion.passValue, {
                shouldValidate: true,
              });
              evaluateFilterOnValidForm({
                prompt: filterSuggestion.prompt,
                passValue: filterSuggestion.passValue,
              });
            }}
          />
        )}
        <div className={formFieldsContainer}>
          <div>
            <Controller
              name="model"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Model is required',
                },
              }}
              disabled={!modelSelectOptions.length}
              render={({ field }) => (
                <>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      evaluateFilterOnValidForm({
                        model: value,
                      });
                    }}
                    value={field.value}
                  >
                    <SelectTrigger
                      ref={field.ref}
                      label="Model"
                      placeholder="Select model"
                      errorMessage={formState.errors.model?.message}
                      className={aiFilterSelectTrigger}
                      variant="outline"
                    />
                    <SelectContent
                      position="popper"
                      className={aiFilterSelectItem}
                      items={modelSelectOptions || []}
                    />
                  </Select>
                </>
              )}
            />
          </div>
          <ManualFilterFormFields
            evaluateFilterOnValidForm={evaluateFilterOnValidForm}
            formData={formData}
          />
          <FilterQualityAlert
            isLoading={isLoadingAiFilterEvaluation}
            aiFilterEvaluation={aiFilterEvaluation}
            error={aiFilterEvaluationError}
          />
        </div>
        <TestAIFilterModal
          disabled={!formState.isValid}
          getFilterValues={getValues}
        />
      </div>
      <footer className={footerContainer}>
        {!isEdit && children}
        <div className={footerButtonsContainer}>
          <ConfirmModal
            isOpen={isConfirmLeaveModalOpen}
            onOpenChange={setIsConfirmLeaveModalOpen}
            title="Are you sure you want to leave?"
            description="You will lose all the changes you made to the filter."
            submitButton={
              <Button onClick={onConfirmLeave} variant="critical" size="small">
                Leave
              </Button>
            }
          >
            <Button onClick={onCancel} variant="secondary">
              Cancel
            </Button>
          </ConfirmModal>

          <Button
            disabled={!isEdit && isFormInvalid}
            loading={formState.isSubmitting}
            type="submit"
            variant="primary"
          >
            Apply filter
          </Button>
        </div>
      </footer>
    </form>
  );
};
